import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import staffAuthService from '../../services/api/staffAuthService';
import { getRastId, setRastId } from '../../utils/rastIdManager';
import './LoginPage.css';

const ROLE_CONFIG = {
  OWNER: {
    title: 'Restaurant Owner Login',
    subtitle: 'Sign in to manage your restaurant dashboard',
    redirectPath: '/dashboard/owner',
    forgotPasswordPath: '/owner/forgot-password'
  },
  CHEF: {
    title: 'Chef Login',
    subtitle: 'Sign in to manage kitchen work tickets',
    redirectPath: '/dashboard/chef'
  },
  WAITER: {
    title: 'Waiter Login',
    subtitle: 'Sign in to manage service work tickets',
    redirectPath: '/dashboard/waiter'
  }
};

const StaffLoginPage = ({ role = 'OWNER' }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const config = useMemo(() => ROLE_CONFIG[role] || ROLE_CONFIG.OWNER, [role]);

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    restaurantId: ''
  });

  useEffect(() => {
    const rastIdFromUrl = searchParams.get('rastId');

    if (rastIdFromUrl) {
      setRastId(rastIdFromUrl);
      setFormData(prev => ({ ...prev, restaurantId: rastIdFromUrl }));
      return;
    }

    const storedRastId = getRastId();
    if (storedRastId) {
      setFormData(prev => ({ ...prev, restaurantId: storedRastId }));
    }
  }, [searchParams]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const validateForm = () => {
    if (!formData.email) {
      setError('Email is required');
      return false;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setError('Please enter a valid email address');
      return false;
    }

    if (!formData.password) {
      setError('Password is required');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError('');

    const result = await staffAuthService.login({
      email: formData.email,
      password: formData.password,
      restaurantId: formData.restaurantId,
      role
    });

    if (result.success) {
      setSuccess(true);
      setTimeout(() => {
        navigate(config.redirectPath, { replace: true });
      }, 800);
    } else {
      setError(result.error || 'Unable to sign in. Please try again.');
    }

    setLoading(false);
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <h1 className="login-title">{config.title}</h1>
          <p className="login-subtitle">{config.subtitle}</p>
        </div>

        {success ? (
          <div className="success-message">
            <div className="success-icon">✓</div>
            <p>You're signed in!</p>
            <p className="redirect-text">Taking you to your dashboard...</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="login-form">
            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="owner@restaurant.com"
                className="form-input"
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="password">Password</label>
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                placeholder="Enter password"
                className="form-input"
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="restaurantId">Restaurant ID (optional)</label>
              <input
                type="text"
                id="restaurantId"
                name="restaurantId"
                value={formData.restaurantId}
                onChange={handleInputChange}
                placeholder="Paste rastId from QR link or restaurant ID"
                className="form-input"
                disabled={loading}
              />
              <p className="help-text">
                Use the same rastId from the customer QR link, e.g. from /start?rastId=...
              </p>
            </div>

            {config.forgotPasswordPath && (
              <p className="help-text">
                Forgot your password? <Link to={config.forgotPasswordPath}>Reset it with email OTP</Link>
              </p>
            )}

            {error && (
              <div className="error-message">
                {error}
              </div>
            )}

            <button
              type="submit"
              className="submit-button"
              disabled={loading}
            >
              {loading ? (
                <span className="loading-text">
                  Signing in
                  <span className="loading-dots">
                    <span>.</span>
                    <span>.</span>
                    <span>.</span>
                  </span>
                </span>
              ) : (
                'Sign In'
              )}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default StaffLoginPage;
